import { useEffect, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { fetchSalespersons } from '@/services/salespersons';
import { fetchSalesForSelect } from '@/services/lookup';
import { saveCommission, updateCommission, type Commission } from '@/services/commissions';

interface CommissionModalProps {
  commission?: Commission | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function CommissionModal({ commission, onClose, onSaved }: CommissionModalProps) {
  const isEditing = !!commission;

  const [salespersons, setSalespersons] = useState<{ id: string; fullName: string }[]>([]);
  const [sales, setSales] = useState<{ id: string; saleNumber: string }[]>([]);
  const [salespersonId, setSalespersonId] = useState(commission?.salespersonId ?? '');
  const [saleId, setSaleId] = useState(commission?.saleId ?? '');
  const [saleAmount, setSaleAmount] = useState(
    commission?.saleAmount != null ? String(commission.saleAmount) : '',
  );
  const [commissionRate, setCommissionRate] = useState(
    commission?.commissionRate != null ? String(commission.commissionRate) : '5',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSalespersons().then((r) => {
      setSalespersons(r.data);
      // Al crear, preselecciona el primer vendedor. Al editar, respeta el valor cargado.
      if (!commission && r.data[0]) setSalespersonId(r.data[0].id);
    });
    fetchSalesForSelect().then((r) => setSales(r));
  }, [commission]);

  const estimatedAmount = (Number(saleAmount) || 0) * ((Number(commissionRate) || 0) / 100);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!salespersonId) {
      setError('Selecciona un vendedor.');
      return;
    }
    if (!saleId) {
      setError('Selecciona una venta.');
      return;
    }
    if ((Number(saleAmount) || 0) <= 0) {
      setError('El monto de venta debe ser mayor a cero.');
      return;
    }

    const input = {
      salespersonId,
      saleId,
      saleAmount: Number(saleAmount) || 0,
      commissionRate: Number(commissionRate) || 0,
    };

    setSaving(true);
    let saveError: string | null;
    if (isEditing && commission) {
      ({ error: saveError } = await updateCommission(commission.id, input));
    } else {
      ({ error: saveError } = await saveCommission(input));
    }
    setSaving(false);

    if (saveError) {
      setError(saveError);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal
      title={isEditing ? 'Editar Comisión' : 'Nueva Comisión'}
      subtitle={
        isEditing
          ? `Editando: ${commission?.salespersonName || '—'}`
          : 'Registra la comisión de un vendedor por venta.'
      }
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className={labelCls}>Vendedor *</label>
          <select className={inputCls} value={salespersonId} onChange={(e) => setSalespersonId(e.target.value)}>
            {salespersons.map((s) => (
              <option key={s.id} value={s.id}>{s.fullName}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Venta *</label>
          <select className={inputCls} value={saleId} onChange={(e) => setSaleId(e.target.value)}>
            <option value="">Selecciona venta...</option>
            {sales.map((s) => (
              <option key={s.id} value={s.id}>{s.saleNumber}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Monto de venta ($)</label>
            <input type="number" min="0" className={inputCls} value={saleAmount} onChange={(e) => setSaleAmount(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Comisión (%)</label>
            <input type="number" min="0" max="100" step="0.5" className={inputCls} value={commissionRate} onChange={(e) => setCommissionRate(e.target.value)} />
          </div>
        </div>

        <div className="flex items-center justify-between bg-accent-50 border border-accent-100 rounded-md px-4 py-3">
          <span className="text-sm text-foreground-700">Comisión estimada</span>
          <span className="text-lg font-bold text-foreground-950">${estimatedAmount.toLocaleString()}</span>
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? (<><i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...</>) : (<><i className="ri-save-line mr-1.5"></i>{isEditing ? 'Guardar cambios' : 'Crear comisión'}</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}